/**
 * Character counters shown below the longer text areas.
 */

import { queryAll } from "../core/dom";
import { text } from "../core/i18n";

export interface CounterState {
    readonly length: number;
    readonly limit: number;
    readonly minimum: number;
    readonly valid: boolean;
}

/** Return the counter state of `field`. */
export function counterState(field: HTMLTextAreaElement | HTMLInputElement): CounterState {
    const limit = Number.parseInt(field.getAttribute("maxlength") ?? "0", 10);
    const minimum = Number.parseInt(field.getAttribute("minlength") ?? "0", 10);
    const length = field.value.length;

    return {
        length,
        limit: Number.isNaN(limit) ? 0 : limit,
        minimum: Number.isNaN(minimum) ? 0 : minimum,
        valid: length === 0 || length >= minimum,
    };
}

/** Return the label rendered next to a counted field. */
export function counterLabel(state: CounterState): string {
    if (state.limit > 0) {
        return text("js.counter.limit", "{count} / {limit} znakov", {
            count: state.length,
            limit: state.limit,
        });
    }

    return text("js.counter.plain", "{count} znakov", { count: state.length });
}

/** Attach a counter element to `field` and keep it in sync. */
export function attachCounter(field: HTMLTextAreaElement | HTMLInputElement): HTMLElement {
    const label = document.createElement("span");
    label.className = "char-counter";
    field.insertAdjacentElement("afterend", label);

    const update = (): void => {
        const state = counterState(field);
        label.textContent = counterLabel(state);
        label.classList.toggle("is-invalid", !state.valid);
    };

    field.addEventListener("input", update);
    update();

    return label;
}

/** Wire every field carrying the `data-counter` attribute. */
export function initCounters(): void {
    for (const field of queryAll<HTMLTextAreaElement>("textarea[data-counter]")) {
        attachCounter(field);
    }
}
